import { useState, type ReactNode } from 'react';
import GuardianDialog, { RELATIONSHIP_LABEL } from './components/GuardianDialog';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { useStudentParent, useParentDigestsList, useParentDigestResend, useGuardianMutations } from '../../api/hooks';
import { ChevronDown, User } from 'lucide-react';
import { Badge, Button, ConfirmDialog, IconButton, Spinner, ErrorPanel, PagedFooter } from '../../components/ui';
import { FaEnvelope, FaPen, FaPlus, FaTelegramPlane, FaTrash } from '../../components/ui/icons';
import type { Guardian, ParentDigestStatus } from '../../api/types';
import { nameOr } from '../../lib/names';
import Box from './components/Box';
import { prettifySlug } from '../../lib/caseStatus';
import { usePagination } from '../../lib/usePagination';
import { dayMonth } from './lib/format';

interface Props { id: number; studentName?: string; }

const SUBHEAD = 'mb-1.5 mt-4 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

const STATUS_TONE: Record<ParentDigestStatus, 'neutral' | 'success' | 'danger' | 'warning'> = {
  not_generated: 'neutral',
  pending: 'warning',
  sent: 'success',
  opened: 'success',
  failed: 'danger',
  opted_out: 'neutral',
};

const STATUS_LABEL: Record<ParentDigestStatus, string> = {
  not_generated: 'Not generated',
  pending: 'Pending',
  sent: 'Sent',
  opened: 'Opened',
  failed: 'Failed',
  opted_out: 'Opted out',
};

/** Whether this guardian gets the digest, and if not, why not. */
function digestState(g: Guardian): { label: string; on: boolean } {
  if (g.opted_out) return { label: 'Unsubscribed', on: false };
  if (!g.email && !g.telegram_linked) return { label: 'No email or Telegram', on: false };
  if (g.digest_on) return { label: 'Gets digest', on: true };
  return g.source === 'student'
    ? { label: 'Waiting for consent', on: false }
    : { label: 'Digest off', on: false };
}

function Dot({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap font-semibold ${on ? 'text-success' : 'text-muted'}`}>
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${on ? 'bg-success' : 'bg-line-strong'}`} />
      {children}
    </span>
  );
}

function GuardianRow({ g, canEdit, onEdit, onRemove }: {
  g: Guardian; canEdit: boolean; onEdit: () => void; onRemove: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const digest = digestState(g);
  const contact = [g.email, g.phone].filter(Boolean).join(' · ');
  const copyLink = async () => {
    if (!g.telegram_link_url) return;
    try {
      await navigator.clipboard.writeText(g.telegram_link_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: nothing to do */ }
  };
  return (
    <li className="flex items-start gap-3 px-3 py-3">
      <span aria-hidden="true" className="mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-full bg-mist text-slate">
        <User size={16} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate font-semibold text-ink">{g.name || 'Unnamed guardian'}</span>
          <Badge tone="neutral">{RELATIONSHIP_LABEL[g.relationship] ?? g.relationship}</Badge>
        </p>
        <p className="truncate text-sm text-muted" title={contact || undefined}>{contact || 'No email or phone on file'}</p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
          <Dot on={digest.on}><FaEnvelope aria-hidden /> {digest.label}</Dot>
          {g.telegram_linked ? (
            <Dot on><FaTelegramPlane aria-hidden /> Telegram linked</Dot>
          ) : canEdit && g.telegram_link_url ? (
            <button type="button" onClick={copyLink}
              className="inline-flex items-center gap-1.5 font-semibold text-slate underline-offset-2 hover:underline">
              <FaTelegramPlane aria-hidden /> {copied ? 'Link copied' : 'Copy Telegram link'}
            </button>
          ) : (
            <Dot on={false}><FaTelegramPlane aria-hidden /> Telegram not linked</Dot>
          )}
          <span className="font-mono text-[11px] text-muted">
            {g.source === 'student' ? 'Added by the student' : `Added by the school${g.added_by_name ? ` · ${g.added_by_name}` : ''}`}
          </span>
        </p>
      </div>
      {canEdit && (
        <div className="flex flex-none gap-1.5">
          <IconButton label={`Edit ${g.name || 'guardian'}`} icon={<FaPen aria-hidden />} onClick={onEdit} />
          <IconButton label={`Remove ${g.name || 'guardian'}`} tone="danger" icon={<FaTrash aria-hidden />} onClick={onRemove} />
        </div>
      )}
    </li>
  );
}

export default function ParentTab({ id, studentName }: Props) {
  const { role } = useAuth();
  // Matches the backend's IsSchoolAdmin gate on the resend endpoint
  // (api/school_panel/views/parent_digest.py) -- narrower than the
  // school_admin/school/admin trio that gates the admin page route.
  const canResend = role === 'school_admin';

  const parent = useStudentParent(id);
  const latest = parent.data?.latest_digest ?? null;

  // The Parent-tab endpoint doesn't expose the underlying ParentDigest row
  // id (only the payload), so a resend needs it resolved from the admin
  // digest list for the same week. Only fetched for school_admin viewers
  // with a digest to resend, since that list endpoint is admin-only.
  const weekLookup = useParentDigestsList(latest?.week_start, { enabled: canResend && !!latest });
  const digestRow = weekLookup.data?.results.find((r) => r.student_id === id) ?? null;
  const resend = useParentDigestResend(latest?.week_start);
  const [busy, setBusy] = useState(false);
  const logPg = usePagination(parent.data?.contact_log ?? [], 5);
  const { remove } = useGuardianMutations(id);
  const [editing, setEditing] = useState<Guardian | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState<Guardian | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleResend = async () => {
    if (digestRow?.id == null) return;
    setBusy(true);
    try {
      await resend.mutateAsync(digestRow.id);
      await parent.refetch();
    } finally {
      setBusy(false);
    }
  };

  if (parent.isLoading) {
    return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  }
  if (parent.isError || !parent.data) {
    return <ErrorPanel onRetry={() => parent.refetch()} />;
  }

  const p = parent.data;
  const canEdit = !!p.can_edit_guardians;
  const maxGuardians = p.max_guardians ?? 4;
  // Older responses carry only the single legacy parent; show it as one guardian.
  const guardians: Guardian[] = p.guardians ?? ((p.parent_name || p.parent_email) ? [{
    id: 0, name: p.parent_name, relationship: 'guardian', email: p.parent_email, phone: '', source: 'student',
    is_primary: true, receives_digest: true, digest_on: p.parent_digest_consent, opted_out: false,
    telegram_linked: p.telegram_linked, added_by_name: '', created_at: '',
  }] : []);
  const recipientNames = (latest?.recipients ?? []).map((r) => r.name).filter(Boolean);

  const channels = latest ? latest.sent_channels.join(' and ') : '';
  const opened = latest?.status === 'opened';

  return (
    <Box
      title="Parent contact"
      help="parent_digest"
      actions={canResend && digestRow?.id != null ? (
        <Button variant="ghost" size="sm" loading={busy} onClick={handleResend}>Resend digest</Button>
      ) : undefined}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className={`${SUBHEAD} !my-0`}>Guardians{guardians.length > 0 && ` · ${guardians.length} of ${maxGuardians}`}</p>
        {canEdit && guardians.length < maxGuardians && (
          <Button variant="ghost" size="sm" className="whitespace-nowrap" icon={<FaPlus aria-hidden />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add guardian
          </Button>
        )}
      </div>
      {guardians.length === 0 ? (
        <p className="text-sm text-muted">
          {canEdit ? 'No guardian on file yet. Add one, or the family can add theirs in the app.' : 'The family has not added a guardian yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-hairline rounded-[12px] border border-line">
          {guardians.map((g) => (
            <GuardianRow key={g.id} g={g} canEdit={canEdit}
              onEdit={() => { setEditing(g); setDialogOpen(true); }} onRemove={() => setRemoving(g)} />
          ))}
        </ul>
      )}
      <p className="mt-2 flex flex-wrap items-center gap-x-2 text-[13px] text-muted">
        Student's digest consent:
        <Dot on={p.parent_digest_consent}>{p.parent_digest_consent ? 'Consented' : 'Not consented'}</Dot>
        <span className="text-xs">(applies to guardians the student added)</span>
      </p>

      <p className={SUBHEAD}>Latest digest</p>
      {!latest ? (
        <p className="text-sm text-muted">None yet: the weekly digest has not been made for this student.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2.5 rounded-[14px] bg-note px-3.5 py-2.5 text-[13px] text-ink">
            <Badge tone={STATUS_TONE[latest.status] ?? 'neutral'}>{STATUS_LABEL[latest.status] ?? prettifySlug(latest.status)}</Badge>
            <span>
              Week of {dayMonth(latest.week_start)}
              {latest.sent_at && ` · sent ${dayMonth(latest.sent_at)}`}
              {channels && ` · via ${channels}`}
              {recipientNames.length > 0 && ` · to ${recipientNames.join(', ')}`}
              {opened && ' · opened'}
            </span>
          </div>

          <details className="group rounded-[12px] border border-line">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-ink">
              What the parent was told
              <ChevronDown size={16} strokeWidth={1.8} aria-hidden className="text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="space-y-4 border-t border-hairline px-3 py-3">
              <dl className="grid gap-2.5 sm:grid-cols-2">
                <div className="rounded-[12px] bg-note px-3 py-2.5">
                  <dt className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">Hours studied</dt>
                  <dd className="mt-1 font-mono text-lg font-semibold text-ink">{latest.payload.hours_studied}</dd>
                </div>
                <div className="rounded-[12px] bg-note px-3 py-2.5">
                  <dt className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">Strongest subject</dt>
                  <dd className="mt-1 text-lg font-semibold text-ink">
                    {latest.payload.strongest_subject ?? '—'}
                  </dd>
                </div>
              </dl>

              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Areas to support</p>
                {latest.payload.areas_to_support.length === 0 ? (
                  <p className="mt-1 text-sm text-muted">None flagged.</p>
                ) : (
                  <ul className="mt-1 list-disc pl-5 text-sm text-ink">
                    {latest.payload.areas_to_support.map((a, i) => (
                      <li key={i}>{a.chapter ?? '—'} ({a.subject ?? '—'}) — {a.mastery_pct ?? '—'}%</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Upcoming exams</p>
                {latest.payload.upcoming_exams.length === 0 ? (
                  <p className="mt-1 text-sm text-muted">None scheduled.</p>
                ) : (
                  <ul className="mt-1 list-disc pl-5 text-sm text-ink">
                    {latest.payload.upcoming_exams.map((e, i) => (
                      <li key={i}>{e.name} ({e.subject ?? '—'}) — due {e.due_date}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-[14px] border-l-[3px] border-accent bg-note p-3.5 text-sm text-ink">
                <p>{latest.payload.encouragement_en}</p>
                <p className="mt-1 text-muted">{latest.payload.encouragement_am}</p>
              </div>
            </div>
          </details>
        </div>
      )}

      <p className={SUBHEAD}>Contact log</p>
      {p.contact_log.length === 0 ? (
        <p className="text-sm text-muted">No contact with the family recorded yet.</p>
      ) : (
        <ul>
          {logPg.pageItems.map((ev) => (
            <li key={ev.id} className="relative pb-3.5 pl-[22px] text-sm text-ink last:pb-0">
              <span aria-hidden="true" className="absolute left-1 top-1.5 h-2 w-2 rounded-full bg-slate" />
              <span aria-hidden="true" className="absolute bottom-0 left-[7.5px] top-[18px] w-px bg-line-strong [li:last-child>&]:hidden" />
              <span className="block font-mono text-[11px] font-medium text-muted">
                {dayMonth(ev.created_at)} · {nameOr(ev.actor_name, ev.actor_username)}
              </span>
              <p>{ev.body}</p>
            </li>
          ))}
        </ul>
      )}
      {p.contact_log.length > 0 && (
        <PagedFooter className="mt-3.5" label="Contact log pagination" {...logPg} onPage={logPg.setPage} />
      )}

      <GuardianDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} studentId={id}
        studentName={studentName} guardian={editing} />
      <ConfirmDialog isOpen={!!removing} title={`Remove ${removing?.name || 'this guardian'}?`}
        body={<>
          {removing?.source === 'student'
            ? 'The student added this guardian. They will stop getting the weekly digest, and the student can add them again.'
            : 'They will stop getting the weekly digest.'}
          {removeError && <span role="alert" className="mt-2 block text-danger-ink">{removeError}</span>}
        </>}
        confirmLabel="Remove" busy={remove.isPending}
        onCancel={() => { setRemoving(null); setRemoveError(null); }}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await remove.mutateAsync(removing.id);
            setRemoving(null);
            setRemoveError(null);
          } catch {
            setRemoveError('Could not remove the guardian. Try again.');
          }
        }} />
    </Box>
  );
}
